import React, { useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import {
  DualEsriView,
  DualEsriViewModel,
  IDualEsriViewModelConstructorProps,
  ViewStyleEnum,
} from 'cd-eft/components/common/mapping';
import {
  Map,
  MapView,
  SceneView,
  Compass,
  Home,
  BasemapGallery,
  Expand,
  LayerList,
  Search,
  ImageryLayer
} from 'cd-eft/utils/esriTypes';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import PortalBasemapsSource from '@arcgis/core/widgets/BasemapGallery/support/PortalBasemapsSource';
import BaseMap from '@arcgis/core/Basemap';
import ImageryTileLayer from '@arcgis/core/layers/ImageryTileLayer';
import ScaleBar from '@arcgis/core/widgets/ScaleBar';
import {mdiCamera} from '@mdi/js';
import stores from 'cd-eft/models/Stores';
import { ClipGrid } from './ClipGrid';
import { ClippedContainerVM } from './ClippedContainerVM';

const { designStore, appConfig } = stores.AppStore;
const ClippedContainerComponent = (props: any) => {
  //initialize the DualEsriViewModel
  const history = useHistory();
  // const location = useLocation();
  const vmREF = useRef<ClippedContainerVM>();
  if (!vmREF.current) {
    vmREF.current = new ClippedContainerVM(history);
  }
  const mapViewRef = useRef<MapView>();
  const sceneViewRef = useRef<SceneView>();
  const vm = vmREF.current;
  const dualEsriViewModelREF = useRef<DualEsriViewModel>();
  if (!dualEsriViewModelREF.current) {
    // const map = new Map({ basemap: 'gray' }); //exclude 3D elevation data
    const map = new Map({ basemap: 'gray-vector', ground: 'world-elevation' }); //include 3D elevation data
    map.ground.opacity = 0.5;
    map.ground.navigationConstraint = { type: 'none' };
    const mapViewA = new MapView({
      map: map,
      center: [-100, 40], // CONTINENTAL US
      zoom: 4, // CONTINENTAL US
    });
    const compassWidgetA = new Compass({ view: mapViewA });
    mapViewA.ui.add({ component: compassWidgetA, position: 'top-left' });
    const sceneViewB = new SceneView({
      map: map,
      center: [-100, 40], // CONTINENTAL US
      zoom: 4, // CONTINENTAL US
    });
    const dualViewProps: IDualEsriViewModelConstructorProps = {
      viewStyle: ViewStyleEnum.corner,
      viewA: mapViewA,
      viewB: sceneViewB,
      mouseTracking: true,
      viewpointTracking: true,
      viewpointSynching: true,
    };
    const dualViewModel = new DualEsriViewModel(dualViewProps);
    ///create reference
    const homeWidget = new Home({ view: mapViewA });
    vm.homeWidget = homeWidget;
    const layerList = new LayerList({ view: mapViewA });
    const layerListWidget = new Expand({
      view: mapViewA,
      content: layerList,
      expandTooltip: 'Layer List',
      collapseTooltip: 'Layer List',
      group: 'top-left',
    });
    // designStore.layerListWidget = layerListWidget;
    const aoiLocator = new Search({ view: mapViewA });
    const aoiWidget = new Expand({
      view: mapViewA,
      content: aoiLocator,
      expandTooltip: 'AOI Locator',
      collapseTooltip: 'AOI Locator',
      group: 'top-left',
    });
    let NAIPLayerTiled = new ImageryTileLayer({url: appConfig.usdaNaipSources.baseMapPath});
    let NAIPLayerNonTiled = new ImageryLayer({url: appConfig.usdaNaipSources.nonTiledPath});
    let NAIPBaseMapTiled = new BaseMap({ 
      baseLayers: [NAIPLayerTiled],
      title: "NAIP Imagery (USDA)", 
      thumbnailUrl: appConfig.usdaNaipSources.thumbnailPath
    });
    let NAIPBaseMapNonTiled = new BaseMap({
      baseLayers: [NAIPLayerNonTiled], 
      title: "NAIP Imagery (USDA)", 
      thumbnailUrl: appConfig.usdaNaipSources.thumbnailPath
    });
    let displayNAIPTiled = false;
    const portalBasemapSource = new PortalBasemapsSource({
      portal: {
        url: "https://www.arcgis.com"
      },
      filterFunction: function (basemap) {
        //hides NAIP Imagery from the Gallery
        return basemap.portalItem.title !== "NAIP Imagery Hybrid";
      },
      updateBasemapsCallback: function (items) {
        // add basemap to the array
        items.unshift(NAIPBaseMapNonTiled);
        // return the array of basemaps
        return items;
      }
    });
    let basemapGallery = new BasemapGallery({
      source: portalBasemapSource,
      view: mapViewA
    });
    const basemapGalleryWidget = new Expand({
      view: mapViewA,
      content: basemapGallery,
      expandTooltip: 'Basemap Gallery',
      collapseTooltip: 'Basemap Gallery',
      group: 'top-left',
    });
    designStore.basemapGalleryWidget = basemapGalleryWidget;
    mapViewA.watch('scale', (newValue) => {
      if (newValue < 4600 && displayNAIPTiled) {
        if (mapViewA.map.basemap.title === 'NAIP Imagery (USDA)') {
          mapViewA.map.basemap = NAIPBaseMapNonTiled;
        };
        displayNAIPTiled = false;
      } else if (newValue >= 4600 && !displayNAIPTiled){
        if (mapViewA.map.basemap.title === 'NAIP Imagery (USDA)') {
          mapViewA.map.basemap = NAIPBaseMapTiled;
        };
        displayNAIPTiled = true;
      }
    })
    const synchToggle = new Expand({
      expandTooltip: 'Synchronize Viewports',
      collapseTooltip: 'Synchronize Viewports',
      expandIconClass: 'esri-icon-link-horizontal',
      collapseIconClass: 'esri-icon-unlocked-link-horizontal',
      group: 'top-left',
    });
    synchToggle.watch('expanded', function (this: Expand) {
      //note: "this" refers to the Expand widget instance
      dualViewModel.viewpointSynching = !this.expanded;
    });
    const rotate3DView = (direction: number) => {
      let heading = 0 ;
      let dir = direction;
      if(designStore.sceneViewActive){
        heading= sceneViewB.camera.heading;
        dir = direction * -1;
      }
      else{
        heading = mapViewA.rotation;
      }
      // Set the heading of the view to the closest multiple of 90 degrees,
      // depending on the direction of rotation
      if (dir > 0) {
        heading = Math.floor((heading + 1e-3) / 90) * 90 + 90;
      } else {
        heading = Math.ceil((heading - 1e-3) / 90) * 90 - 90;
      }
      if(designStore.sceneViewActive){
        sceneViewB
        .goTo({
          heading: heading,
        })
        .catch((error) => {
          if (error.name != 'AbortError') {
            console.error(error);
          }
        });
      }
      else{
        mapViewA.rotation = heading;
      }
    }
    const rotateCW = new Expand({
      expandTooltip: 'Rotate Clockwise',
      collapseTooltip: 'Rotate Clockwise',
      expandIconClass: 'esri-icon-rotate',
      collapseIconClass: 'esri-icon-rotate',
    });
    rotateCW.watch('expanded', function (this: Expand) {
      rotate3DView(1);
    });

    const rotateCCW = new Expand({
      expandTooltip: 'Rotate Counter Clockwise',
      collapseTooltip: 'Rotate Counter Clockwise',
      expandIconClass: 'esri-icon-rotate-back',
      collapseIconClass: 'esri-icon-rotate-back',
    });
    rotateCCW.watch('expanded', function (this: Expand) {
      rotate3DView(-1);
    });
    const viewSwitcher = new Expand({
      expandTooltip: 'Switch Primary Viewport',
      collapseTooltip: 'Switch Primary Viewport',
      expandIconClass: 'esri-icon-public',
      collapseIconClass: 'esri-icon-maps',
      group: 'top-left',
    });
    const iconPath = mdiCamera;
    
    const takeScreenshot = document.createElement("button");
    takeScreenshot.title = 'Take Screenshot';
    takeScreenshot.className = "esri-widget--button snap-to-point";
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '24'); // Adjust icon size
    svg.setAttribute('height', '24');
    svg.setAttribute('style', 'fill: #6e6e6e');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', iconPath);
    svg.appendChild(path);
    takeScreenshot.appendChild(svg);
    document.body.appendChild(takeScreenshot);
    takeScreenshot.addEventListener('click', function() {
    });
    const scaleBar = new ScaleBar({
      view: dualViewModel.primaryView,
      id: 'scale-ruler',
      style: 'ruler',
      unit: 'metric'
    });
    dualViewModel.primaryView.ui.add(scaleBar, 'bottom-left');
    viewSwitcher.watch('expanded', function (this: Expand) {
      stores.AppStore.designStore.sceneViewActive = !stores.AppStore.designStore.sceneViewActive;
      //note: "this" refers to the Expand widget instance
      dualViewModel.switchPrimaryView = this.expanded;
    });
    dualViewModel.primaryView.ui.add({ component: viewSwitcher, position: 'top-left', index: 2 });
    dualViewModel.primaryView.ui.add({ component: synchToggle, position: 'top-left', index: 3 });
    dualViewModel.primaryView.ui.add({ component: takeScreenshot, position: 'top-left', index: 4 });
    dualViewModel.primaryView.ui.add({ component: basemapGalleryWidget, position: 'top-left', index: 5 });
    dualViewModel.primaryView.ui.add({ component: layerListWidget, position: 'top-left', index: 6 });
    dualViewModel.primaryView.ui.add({ component: homeWidget, position: 'top-left', index: 7 });
    dualViewModel.primaryView.ui.add({ component: rotateCW, position: 'top-left', index: 8 });
    dualViewModel.primaryView.ui.add({ component: rotateCCW, position: 'top-left', index: 9 });
    dualViewModel.primaryView.ui.add({ component: aoiWidget, position: 'top-left', index: 10 });
    dualViewModel.viewpointTracking = false;
    dualViewModel.mouseTracking = false;
    dualEsriViewModelREF.current = dualViewModel;
    mapViewRef.current = mapViewA;
    sceneViewRef.current = sceneViewB;
    EngineeringMapService.Load(mapViewA);
    vm.onMapViewMounted(mapViewA);
  }
  useEffect(function mount() {
    MessageCenter.resetMessageCenter()
    function unmount() {
      designStore.snappingEnabled = false;
      designStore.toggle3DMap = false  
    }
    return unmount;
  }, []); // important, only run mount/unmount functions once to manage the map lifecycle
  return (
    <div className="design-container" id="design-container">
      <div className="design-workspace">
        <div className="design-mapping">
          <DualEsriView vm={dualEsriViewModelREF.current} />
        </div>
         <ClipGrid  designREF={vm}   />
        </div>
    </div>
  );
};
export const ClippedContainer = observer(ClippedContainerComponent);